import React from "react";

import { addYears, format, isValid, parse, startOfDay } from "date-fns";
import { enUS, fr } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/src/components/ui/button";
import { Calendar } from "@/src/components/ui/calendar";
import { Input } from "@/src/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/src/components/ui/popover";
import { cn } from "@/src/lib/utils";

import type { Locale } from "date-fns";
import type { Matcher } from "react-day-picker";

const DATE_DISPLAY_FORMAT = "PPP";
const DATE_PARSE_FORMAT_LIST = ["P", "PP", "PPP", "PPPP", "yyyy-MM-dd"];

function parseUserDate(rawValue: string, locale: Locale): Date | undefined {
  const trimmedValue = rawValue.trim();

  if (!trimmedValue) return undefined;

  return DATE_PARSE_FORMAT_LIST.map((parseFormat) =>
    parse(trimmedValue, parseFormat, new Date(), { locale }),
  ).find((candidateDate) => isValid(candidateDate));
}

function isDateWithinBounds(
  date: Date,
  minDate?: Date,
  maxDate?: Date,
): boolean {
  const day = startOfDay(date).getTime();

  if (minDate && day < startOfDay(minDate).getTime()) return false;
  if (maxDate && day > startOfDay(maxDate).getTime()) return false;

  return true;
}

interface DateTimePickerProps {
  disabled?: boolean;
  lang?: string;
  onDateTimeChange: (date: Date) => void;
  value?: Date;
  minDate?: Date;
  maxDate?: Date;
  placeholder?: string;
  withoutTime?: boolean;
  className?: string;
  popoverClassName?: string;
  popoverContainer?: HTMLElement | null;
}

export default function DateTimePicker({
  disabled,
  lang,
  onDateTimeChange,
  value,
  minDate,
  maxDate,
  placeholder,
  withoutTime,
  className,
  popoverClassName,
  popoverContainer,
}: DateTimePickerProps): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  const [month, setMonth] = React.useState<Date | undefined>(value);

  const { t, i18n } = useTranslation("editor-smartlist");
  const locale = (lang ?? i18n.language) === "fr" ? fr : enUS;

  const inputRef = React.useRef<HTMLInputElement>(null);
  const formatDateValue = React.useCallback(
    (date?: Date): string =>
      date ? format(date, DATE_DISPLAY_FORMAT, { locale }) : "",
    [locale],
  );
  const [inputValue, setInputValue] = React.useState(() =>
    formatDateValue(value),
  );

  // Resync the displayed text with the controlled value, unless the user is
  // currently typing in the field (so we never reformat mid-edit).
  React.useEffect(() => {
    if (document.activeElement === inputRef.current) return;

    setInputValue(formatDateValue(value));
    setMonth(value);
  }, [value, formatDateValue]);

  const time = value ? format(value, "HH:mm") : "00:00";
  const disabledDays = [
    minDate ? { before: minDate } : undefined,
    maxDate ? { after: maxDate } : undefined,
  ].filter(Boolean) as Matcher[];
  const calendarEndMonth = maxDate ?? addYears(new Date(), 100);

  const applyDate = (selectedDate: Date): void => {
    const nextDate = new Date(selectedDate);

    if (value) {
      nextDate.setHours(value.getHours(), value.getMinutes(), 0, 0);
    }

    onDateTimeChange(nextDate);
  };

  const handleInputChange = (rawValue: string): void => {
    setInputValue(rawValue);

    const parsedDate = parseUserDate(rawValue, locale);

    if (!parsedDate || !isDateWithinBounds(parsedDate, minDate, maxDate))
      return;

    setMonth(parsedDate);
    applyDate(parsedDate);
  };

  const handleInputBlur = (): void => {
    setInputValue(formatDateValue(value));
    setMonth(value);
  };

  const handleTimeChange = (timeValue: string): void => {
    const [hours, minutes] = timeValue.split(":").map(Number);

    if (hours === undefined || minutes === undefined) return;

    const nextDate = new Date(value ?? new Date());

    nextDate.setHours(hours, minutes, 0, 0);
    onDateTimeChange(nextDate);
  };

  const inputClassNames = cn(
    "bg-card border-border pr-9 focus-visible:border-primary focus-visible:shadow-none focus-visible:ring-0",
    {
      "border-primary": open,
    },
    className,
  );

  const timeInputClassNames = cn(
    "bg-card w-fit flex justify-center focus-visible:border-primary cursor-pointer appearance-none border-border px-1.5 focus-visible:shadow-none focus-visible:ring-0 [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none",
  );

  const defaultPlaceholder = new Date().toLocaleDateString();

  return (
    <div className="flex w-72 flex-row justify-between gap-2">
      <div className="relative flex-1">
        <Input
          ref={inputRef}
          className={inputClassNames}
          disabled={disabled}
          id="date-picker"
          onBlur={handleInputBlur}
          placeholder={placeholder ?? defaultPlaceholder}
          value={inputValue}
          onChange={(e) => {
            handleInputChange(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setOpen(true);
            }
          }}
        />
        <Popover onOpenChange={setOpen} open={open}>
          <PopoverTrigger asChild>
            <Button
              aria-label={t("selectDate")}
              className="absolute top-1/2 right-1 size-7 -translate-y-1/2 cursor-pointer"
              disabled={disabled}
              type="button"
              variant="ghost"
            >
              <CalendarIcon className="size-3.5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            alignOffset={-8}
            className={cn("w-auto overflow-hidden p-0", popoverClassName)}
            container={popoverContainer}
            sideOffset={10}
          >
            <Calendar
              captionLayout="dropdown"
              disabled={disabledDays}
              endMonth={calendarEndMonth}
              locale={locale}
              mode="single"
              month={month}
              onMonthChange={setMonth}
              selected={value}
              startMonth={minDate}
              onSelect={(selectedDate) => {
                if (!selectedDate) return;

                applyDate(selectedDate);
                setOpen(false);
              }}
            />
          </PopoverContent>
        </Popover>
      </div>
      {!withoutTime ? (
        <Input
          className={timeInputClassNames}
          disabled={disabled}
          type="time"
          value={time}
          onChange={(e) => {
            handleTimeChange(e.target.value);
          }}
        />
      ) : null}
    </div>
  );
}
